// Pinned-jobs storage — shared between TodayPage (renders the tray) and
// PersonPage (renders the star button). localStorage-backed v1 so we
// don't need a server round-trip on every star-toggle. A storage event
// fires on toggle so any other open Today tab updates without a refresh.
//
// v2 (when multi-staff matters) moves this to a server-backed table
// keyed by staff identifier. Today's single-shared-password auth model
// means localStorage is fine — every staffer is effectively the same
// principal at the API layer, and pins are personal-use markers anyway.

export const PINNED_KEY = "njrc.pinnedJobs";

export interface PinnedRecord {
  jobId: string;
  name: string;
  dob: string;
  pinnedAt: string;
}

export function readPinned(): PinnedRecord[] {
  try {
    const raw = localStorage.getItem(PINNED_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as PinnedRecord[];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

export function writePinned(pins: PinnedRecord[]): void {
  try { localStorage.setItem(PINNED_KEY, JSON.stringify(pins)); } catch { /* full storage — ignore */ }
}

/** Toggle pinned. Returns the new state (true=now pinned). Caps at 12
 *  pins so the tray doesn't grow unbounded — older pins fall off the
 *  end first (FIFO by recency). */
export function togglePinned(job: { jobId: string; name: string; dob: string }): boolean {
  const cur = readPinned();
  const has = cur.some((p) => p.jobId === job.jobId);
  let next: PinnedRecord[];
  if (has) {
    next = cur.filter((p) => p.jobId !== job.jobId);
  } else {
    next = [{ ...job, pinnedAt: new Date().toISOString() }, ...cur].slice(0, 12);
  }
  writePinned(next);
  // Manually fire a storage event for SAME-tab listeners — the native
  // `storage` event only fires across tabs by default. Without this,
  // pinning on PersonPage wouldn't update the Today tray in the same
  // tab without a reload.
  try {
    window.dispatchEvent(new StorageEvent("storage", {
      key: PINNED_KEY,
      newValue: JSON.stringify(next),
    }));
  } catch { /* older browsers — no-op */ }
  return !has;
}

export function isPinned(jobId: string): boolean {
  return readPinned().some((p) => p.jobId === jobId);
}
